/**
 * SlopeChartRenderer — line chart with exactly two x-points and label-at-end.
 *
 * The clearest possible "what changed FY23 → FY24?" answer. Each line is one
 * entity; the slope makes direction-of-change immediately obvious; labels at
 * the right edge tell you which entity is which without legend hunting.
 *
 * Also pairs with the rank-change variant (Phase H4) where the y-axis is rank
 * (1=best) instead of value — same renderer, different prep.
 *
 * Phase C5 of issue #90 v3 plan.
 */
import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import type { PerfRow, MetricKey, GroupByDimension } from './types'
import { METRIC_LABELS, formatMetric, getDimensionLabel, getDimensionKey } from './types'
import { useExecChartTheme } from './echarts-theme'

interface Props {
  data: PerfRow[]
  /** Dimension to compare BETWEEN (the two x-points), e.g., 'fiscal_year'. */
  xAxis: GroupByDimension
  /** Dimension that identifies each line, e.g., 'macom'. */
  groupBy: GroupByDimension
  metrics: MetricKey[]
  /**
   * If true, y-axis is rank (1 = best) instead of value. Used by the
   * rank-change slope variant. Default: false (value).
   */
  asRank?: boolean
  /** Limit to top-N most-changed entities (by |v1 - v0|) for legibility. */
  topN?: number
}

export default function SlopeChartRenderer({ data, xAxis, groupBy, metrics, asRank = false, topN = 8 }: Props) {
  const theme = useExecChartTheme()
  const metric = metrics[0]

  const { categories, series } = useMemo(() => {
    // Pick the two most-populous x-values. Naïve "first and last" can pick
    // the boundary FYs in a 3-FY range where the newest is partial-year, so
    // many entities have null at one endpoint and fall out of the slope.
    const counts = new Map<string, number>()
    for (const r of data) {
      const k = String(r[xAxis] ?? '')
      if (!k) continue
      counts.set(k, (counts.get(k) || 0) + 1)
    }
    if (counts.size < 2) {
      return { categories: [] as string[], series: [] as any[] }
    }
    const sortedByCount = [...counts.entries()].sort((a, b) => b[1] - a[1])
    const top2 = sortedByCount.slice(0, 2).map(([k]) => k).sort()
    const x0 = top2[0]
    const x1 = top2[1]
    const cats = [String(x0), String(x1)]

    const byEntity = new Map<string, { label: string; v0: number | null; v1: number | null }>()
    for (const row of data) {
      const xv = String(row[xAxis] ?? '')
      const ekey = getDimensionKey(row, groupBy)
      if (!ekey) continue
      const v = row[metric]
      const num = typeof v === 'number' ? v : parseFloat(String(v))
      if (Number.isNaN(num)) continue
      if (!byEntity.has(ekey)) {
        byEntity.set(ekey, { label: getDimensionLabel(row, groupBy), v0: null, v1: null })
      }
      const e = byEntity.get(ekey)!
      if (xv === x0) e.v0 = num
      else if (xv === x1) e.v1 = num
    }

    let entities = Array.from(byEntity.values())
    // Drop entities missing either endpoint — can't draw a slope.
    entities = entities.filter((e) => e.v0 != null && e.v1 != null)
    // Keep the top-N entities by absolute change so labels don't overlap.
    if (topN && entities.length > topN) {
      entities.sort((a, b) => Math.abs((b.v1 ?? 0) - (b.v0 ?? 0)) - Math.abs((a.v1 ?? 0) - (a.v0 ?? 0)))
      entities = entities.slice(0, topN)
    }
    if (asRank) {
      // Rank by v at each x-point (1 = best = highest value; lower-is-better
      // callers can invert before passing data).
      const sortedByV0 = [...entities].sort((a, b) => (b.v0 ?? -Infinity) - (a.v0 ?? -Infinity))
      const sortedByV1 = [...entities].sort((a, b) => (b.v1 ?? -Infinity) - (a.v1 ?? -Infinity))
      const r0 = new Map(sortedByV0.map((e, i) => [e.label, i + 1]))
      const r1 = new Map(sortedByV1.map((e, i) => [e.label, i + 1]))
      entities = entities.map((e) => ({ ...e, v0: r0.get(e.label) ?? null, v1: r1.get(e.label) ?? null }))
    }

    // Truncate long entity labels so they don't run off the chart edge.
    const truncate = (s: string, n = 22) => (s.length > n ? s.slice(0, n - 1) + '…' : s)
    const fmtVal = (v: number | null) => {
      if (v == null) return '—'
      return asRank ? `#${v}` : formatMetric(metric, v)
    }

    const ser = entities.map((e) => ({
      name: e.label,
      type: 'line' as const,
      data: [e.v0, e.v1],
      symbolSize: 8,
      // Show only an endLabel (right side) — drawing labels on both endpoints
      // collides badly when many lines share similar values. The endLabel
      // carries name + value; the left-side value is in the tooltip on hover.
      endLabel: {
        show: true,
        formatter: () => `${truncate(e.label)}  ${fmtVal(e.v1)}`,
        fontSize: 10,
        distance: 8,
      },
      // hideOverlap drops colliding endLabels in dense regions rather than
      // letting them stack on top of each other.
      labelLayout: { hideOverlap: true },
      lineStyle: { width: 1.5 },
    }))

    return { categories: cats, series: ser }
  }, [data, xAxis, groupBy, metric, asRank])

  if (categories.length === 0 || series.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        Need at least two {xAxis} values to render a slope chart
      </div>
    )
  }

  const option: any = {
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => {
        const items = Array.isArray(params) ? params : [params]
        const header = items[0]?.axisValue || ''
        const lines = items.map((p: any) => {
          const v = p.value
          const formatted = asRank
            ? (typeof v === 'number' ? `Rank ${v}` : '—')
            : formatMetric(metric, v)
          return `${p.marker} ${p.seriesName}: <b>${formatted}</b>`
        })
        return `<b>${header}</b><br/>${lines.join('<br/>')}`
      },
    },
    // Wide right-side gutter to give endLabels room to breathe without
     // clipping at the chart edge.
    grid: { left: 50, right: 220, top: 20, bottom: 40 },
    xAxis: { type: 'category', data: categories, axisLabel: { fontSize: 10 }, boundaryGap: false },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 10 },
      inverse: asRank, // rank 1 at top
      name: asRank ? 'Rank' : METRIC_LABELS[metric],
      nameTextStyle: { fontSize: 10 },
    },
    legend: { show: false },
    series,
  }

  return (
    <ReactECharts
      option={option}
      theme={theme}
      style={{ height: '100%', width: '100%' }}
      opts={{ renderer: 'canvas' }}
    />
  )
}
